import { useLocation } from '@gatsbyjs/reach-router';
import { navigate } from 'gatsby';
import { useCallback, useEffect, useState } from 'react';

type UseQueryParamReturn = {
  value: string | null;
  setValue: (value: string | null, clearOthers?: boolean) => void;
};

export const useQueryParam = (paramName: string, defaultValue?: string): UseQueryParamReturn => {
  const location = useLocation();

  // Helper to safely get query param value
  const getQueryParamValue = useCallback((name: string): string | null => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }, []);

  // Initialize state with current URL value
  const [value, setValueState] = useState<string | null>(() => {
    return getQueryParamValue(paramName);
  });

  // Update URL and state
  const setValue = useCallback(
    (newValue: string | null, clearOthers = false) => {
      if (typeof window === 'undefined') return;

      // If clearOthers is true, start with empty params; otherwise, keep existing params
      const params = clearOthers
        ? new URLSearchParams()
        : new URLSearchParams(window.location.search);

      if (newValue === null || newValue === '') {
        params.delete(paramName);
      } else {
        params.set(paramName, newValue);
      }

      const newSearch = params.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Update local state
      setValueState(newValue);

      // Update URL without adding to history
      void navigate(newUrl, { replace: true });
    },
    [paramName],
  );

  // Handle browser navigation (back/forward)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePopState = () => {
      const newValue = getQueryParamValue(paramName);
      setValueState(newValue);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [paramName, getQueryParamValue]);

  // Sync with URL changes on mount, param name change, and location change
  useEffect(() => {
    const currentValue = getQueryParamValue(paramName);
    setValueState(currentValue);
  }, [paramName, getQueryParamValue, location.search]);

  return {
    value: value ?? defaultValue ?? null,
    setValue,
  };
};
